import { useState } from 'react';
import { adPlacements } from '../../../mocks/adPlacements';

interface AdSubmissionFormProps {
  selectedPlacement: string | null;
  onClose: () => void;
}

export default function AdSubmissionForm({ selectedPlacement, onClose }: AdSubmissionFormProps) {
  const [formData, setFormData] = useState({
    placementId: selectedPlacement || '',
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    adCreativeUrl: '',
    targetUrl: '',
    duration: '1',
    startDate: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedPlacementData = adPlacements.find(p => p.id === formData.placementId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    alert('Ad submission received! Our team will contact you within 24 hours to finalize your campaign.');
    setIsSubmitting(false);
    onClose();
  };

  const calculateTotal = () => {
    if (!selectedPlacementData) return 0;
    const months = parseInt(formData.duration);
    const basePrice = selectedPlacementData.price * months;
    
    // Apply discounts
    if (months >= 12) return basePrice * 0.8; // 20% off
    if (months >= 6) return basePrice * 0.85; // 15% off
    if (months >= 3) return basePrice * 0.9; // 10% off
    return basePrice;
  };

  return (
    <form onSubmit={handleSubmit} className="p-6">
      {/* Placement Selection */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Select Ad Placement *
        </label>
        <select
          required
          value={formData.placementId}
          onChange={(e) => setFormData({ ...formData, placementId: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        >
          <option value="">Choose a placement...</option>
          {adPlacements
            .filter(p => p.status === 'available')
            .map(placement => (
              <option key={placement.id} value={placement.id}>
                {placement.name} - ${placement.price}/month ({placement.dimensions})
              </option>
            ))}
        </select>
      </div>

      {/* Selected Placement Info */}
      {selectedPlacementData && (
        <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">{selectedPlacementData.name}</h3>
              <p className="text-sm text-gray-600 mb-2">{selectedPlacementData.position}</p>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-600">
                  <i className="ri-aspect-ratio-line mr-1"></i>
                  {selectedPlacementData.dimensions}
                </span>
                <span className="text-emerald-600 font-semibold">
                  ${selectedPlacementData.price}/month
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Business Information */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Business Name *
            </label>
            <input
              type="text"
              required
              value={formData.businessName}
              onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Your Business Name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Name *
            </label>
            <input
              type="text"
              required
              value={formData.contactName}
              onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Your Name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="your@email.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="(916) 555-0123"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website URL
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="https://yourbusiness.com"
            />
          </div>
        </div>
      </div>

      {/* Ad Creative */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Ad Creative</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ad Creative Image URL
            </label>
            <input
              type="url"
              value={formData.adCreativeUrl}
              onChange={(e) => setFormData({ ...formData, adCreativeUrl: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="https://example.com/ad-image.jpg"
            />
            <p className="text-xs text-gray-500 mt-1">
              Don't have an ad creative? Our team can design one for you at no extra cost.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target URL (Where clicks should go) *
            </label>
            <input
              type="url"
              required
              value={formData.targetUrl}
              onChange={(e) => setFormData({ ...formData, targetUrl: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="https://yourbusiness.com/special-offer"
            />
          </div>
        </div>
      </div>

      {/* Campaign Duration */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaign Duration</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duration (Months) *
            </label>
            <select
              required
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="1">1 Month</option>
              <option value="3">3 Months (Save 10%)</option>
              <option value="6">6 Months (Save 15%)</option>
              <option value="12">12 Months (Save 20%)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Start Date *
            </label>
            <input
              type="date"
              required
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>
      </div>

      {/* Additional Message */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Additional Information
        </label>
        <textarea
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          placeholder="Any special requirements or questions..."
        />
      </div>

      {/* Price Summary */}
      {selectedPlacementData && (
        <div className="mb-6 bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Price Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Base Price ({formData.duration} month{parseInt(formData.duration) > 1 ? 's' : ''})</span>
              <span className="font-medium">${(selectedPlacementData.price * parseInt(formData.duration)).toLocaleString()}</span>
            </div>
            {parseInt(formData.duration) >= 3 && (
              <div className="flex justify-between text-sm text-emerald-600">
                <span>Multi-month Discount</span>
                <span>-${((selectedPlacementData.price * parseInt(formData.duration)) - calculateTotal()).toLocaleString()}</span>
              </div>
            )}
            <div className="pt-3 border-t border-gray-300 flex justify-between">
              <span className="font-semibold text-gray-900">Total</span>
              <span className="text-2xl font-bold text-emerald-600">${calculateTotal().toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !formData.placementId}
          className="flex-1 bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              Processing...
            </span>
          ) : (
            'Submit Ad Request'
          )}
        </button>
      </div>
    </form>
  );
}